/**
 * ProviderSurfaceGate — the once-per-session XOR switch.
 *
 * Locked 2026-05-30 — Provider Dual-Surface Architecture.
 * Locked 2026-05-31 — User-Blocker Pass: switched to `effectiveMode` from
 * ViewModeContext so the gate, the CSS overrides, and the viewport meta
 * all agree on a single resolved value. No more split-brain between gate
 * and viewport.
 *
 * Resolution: ViewModeContext.effectiveMode is the only input.
 *   - effectiveMode = 'mobile'  → Provider Mobile surface
 *   - effectiveMode = 'desktop' → Provider Desktop surface
 *
 * Critical: switching mode causes a key change on the wrapper, which forces
 * React to unmount the old surface tree completely and mount the new one
 * fresh. State cannot bleed because there is no shared subtree.
 */
import React, { Suspense } from 'react';
import { useViewMode } from '@/contexts/ViewModeContext';
import OnboardingEnforcementGate from '@/components/OnboardingEnforcementGate';

const ProviderMobileApp = React.lazy(() => import('@/components/ProviderMobileApp'));
const ProviderDesktopApp = React.lazy(() => import('@/components/ProviderDesktopApp'));

const SurfaceLoading = () => (
  <div
    data-testid="provider-surface-loading"
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0f172a',
      color: '#cbd5e1',
      fontSize: 14,
    }}
  >
    Loading…
  </div>
);

const ProviderSurfaceGate = ({ ariMode }) => {
  const { effectiveMode } = useViewMode();
  const surface = effectiveMode === 'desktop' ? 'desktop' : 'mobile';

  return (
    <Suspense fallback={<SurfaceLoading />}>
      {/* The `key` is the architectural enforcement: when surface changes,
          React unmounts the previous surface tree completely and mounts
          the new one fresh. No DOM is shared between them. */}
      <div key={`provider-surface-${surface}`} data-testid={`provider-surface-${surface}`}>
        <OnboardingEnforcementGate>
          {surface === 'desktop' ? (
            <ProviderDesktopApp ariMode={ariMode} />
          ) : (
            <ProviderMobileApp ariMode={ariMode} />
          )}
        </OnboardingEnforcementGate>
      </div>
    </Suspense>
  );
};

export default ProviderSurfaceGate;
